"use client";

import clsx from "classnames";
import { Button, Col, Container, Dropdown, FormControl, FormGroup, Row, Stack } from "react-bootstrap";
import { BlogFeedCard, Title } from "@/ui";
import { getRoute } from "@/getters/getRoute";
import classes from "./styles.module.scss";
import { useMemo, useState, useEffect } from "react";
import { useOptionFilter } from "@/hooks/useOptionFilter";
import useBlogs from "@/hooks/useBlogs";
import useBlogTags from "@/hooks/useBlogTags";
import sector from "@/../.sourceflow/sector.json";

/* -------------------------------- helpers -------------------------------- */

const getCareerAdviceCategoryFromPath = () => {
  if (typeof window === "undefined") return null;

  const path = window.location.pathname;

  const categories = ["career-support", "career-progression", "cv-templates", "employment-rights"];

  const match = categories.find((cat) => path.includes(`/career-advice/${cat}`));

  return match || null;
};

const norm = (v) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

const intersects = (a = [], b = []) => {
  if (!a?.length || !b?.length) return false;
  const set = new Set(a.map(String));
  return b.some((x) => set.has(String(x)));
};

const toStringArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val.map(String);
  if (val?.en && Array.isArray(val.en)) return val.en.map(String);
  return [String(val)];
};

const getSectorIdsFromArticle = (item) => {
  if (Array.isArray(item?.related_sector)) return item.related_sector.map(String);
  return toStringArray(item?.related_sector);
};

const getTagIdsFromArticle = (item) => toStringArray(item?.tags);

const getPublishDate = (item) => {
  const raw = item?.publish_date || item?.date;
  if (!raw) return new Date(0);
  return new Date(raw);
};

/* ------------------------------- component -------------------------------- */

export default function ArticleFeed({
  sectionId,
  className,
  bgColor,
  title,
  titleColor,
  source,
  filterTags,
  sectors,
  perPage = 9,
  showSearchField,
  showCategoryFilter,
  showTagFilter,
  showSectorFilter,
  showLoadMore = true,
}) {
  const blogs = useBlogs({ source }) || [];
  const _blogTags = useBlogTags() || [];
  const currentCategory = useMemo(() => getCareerAdviceCategoryFromPath(), []);
  /* --------------------------- SORT --------------------------- */

  const allItems = useMemo(() => {
    return [...blogs].sort((a, b) => getPublishDate(b) - getPublishDate(a));
  }, [blogs]);

  /* --------------------------- FILTER IDS --------------------------- */

  const sectorFilterIds = useMemo(
    () => (Array.isArray(sectors) ? sectors.map(String) : sectors ? [String(sectors)] : []),
    [sectors],
  );

  const filterTagIds = useMemo(
    () => (Array.isArray(filterTags) ? filterTags.map(String) : filterTags ? [String(filterTags)] : []),
    [filterTags],
  );

  /* --------------------------- TAG FILTER --------------------------- */

  const _afterTags = useMemo(() => {
    return allItems.filter((i) => (!filterTagIds.length ? true : intersects(getTagIdsFromArticle(i), filterTagIds)));
  }, [allItems, filterTagIds]);

  /* --------------------------- SECTOR DROPDOWN OPTIONS --------------------------- */

  const sectorOptions = useMemo(() => {
    // get all sector IDs used in current articles
    const usedSectorIds = new Set();

    _afterTags.forEach((item) => {
      getSectorIdsFromArticle(item).forEach((id) => {
        usedSectorIds.add(String(id));
      });
    });

    // map only matching sectors
    return (sector?.category_values || [])
      .filter((s) => usedSectorIds.has(String(s.id)))
      .map((s) => ({
        id: String(s.id),
        title: s?.title?.en || s?.title || "Unknown",
      }));
  }, [_afterTags]);

  const [selectedSector, setSelectedSector] = useState(null);

  /* --------------------------- SECTOR FILTER --------------------------- */

  const _prefiltered = useMemo(() => {
    let items = _afterTags;

    // existing sector prop filter
    if (sectorFilterIds.length) {
      items = items.filter((i) => intersects(getSectorIdsFromArticle(i), sectorFilterIds));
    }

    // NEW: dropdown sector filter
    if (selectedSector) {
      items = items.filter((i) => getSectorIdsFromArticle(i).includes(String(selectedSector)));
    }

    return items;
  }, [_afterTags, sectorFilterIds, selectedSector]);

  /* --------------------------- SEARCH --------------------------- */

  const [term, setTerm] = useState("");

  const _afterSearch = useMemo(() => {
    const t = norm(term);
    if (!t) return _prefiltered;

    return _prefiltered.filter((i) => norm(i.title).includes(t));
  }, [_prefiltered, term]);

  /* --------------------------- PAGINATION --------------------------- */

  const [limit, setLimit] = useState(perPage);

  const visibleItems = useMemo(() => {
    return _afterSearch.slice(0, limit);
  }, [_afterSearch, limit]);

  useEffect(() => {
    setLimit(perPage);
  }, [perPage, term, sectorFilterIds, filterTagIds, selectedSector]);

  const loadMore = () => {
    setLimit((prev) => prev + perPage);
  };

  /* --------------------------- CATEGORY / TAG FILTER --------------------------- */

  const [categories, categoryFilter, setCategoryFilter] = useOptionFilter(_afterSearch, "related_category", () => {});

  const [tags, tagFilter, setTagFilter] = useOptionFilter(_afterSearch, "tags", () => {});

  const tagsList = useMemo(() => {
    const src = Array.isArray(tags) ? tags : Array.from(tags || []);
    return src.map((tag) => ({
      id: tag,
      title: _blogTags.find((i) => String(i.id) === String(tag))?.tag || String(tag),
    }));
  }, [tags, _blogTags]);

  const tagFilterName = tagsList.find((i) => i.id === tagFilter)?.title ?? null;

  /* --------------------------- UI --------------------------- */

  return (
    <div id={sectionId} className={clsx(className, bgColor, classes.feed)}>
      <Container className="mw-xxl">
        {title && <Title title={title} className={clsx(titleColor, "text-center text-md-start")} />}
        {(showSearchField || showCategoryFilter || showTagFilter || showSectorFilter) && (
          <Stack className={clsx(classes.feed__filters, "p-4 mb-4")}>
            <Row>
              {/* SEARCH */}
              {showSearchField && (
                <Col xs={12} md>
                  <FormGroup>
                    <label>Search</label>
                    <FormControl
                      className="mt-3"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      placeholder="Search..."
                    />
                  </FormGroup>
                </Col>
              )}

              {/* CATEGORY */}
              {showCategoryFilter && (
                <Col xs={12} md>
                  <Dropdown>
                    <Dropdown.Header>Categories</Dropdown.Header>
                    <Dropdown.Toggle className="mt-3" variant="white">
                      {categoryFilter ?? "Select Category"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setCategoryFilter(null)}>Select Category</Dropdown.Item>
                      {categories.map((category, key) => (
                        <Dropdown.Item key={key} onClick={() => setCategoryFilter(category)}>
                          {category}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              )}

              {/* TAGS */}
              {showTagFilter && (
                <Col xs={12} md>
                  <Dropdown>
                    <Dropdown.Header className="mb-3">Tags</Dropdown.Header>
                    <Dropdown.Toggle variant="white">{tagFilterName ?? "Select Tag"}</Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setTagFilter(null)}>Select Tag</Dropdown.Item>
                      {tagsList.map((tag, key) => (
                        <Dropdown.Item key={key} onClick={() => setTagFilter(tag.id)}>
                          {tag.title}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              )}

              {/* SECTOR */}
              {showSectorFilter && (
                <Col xs={12} md>
                  <Dropdown>
                    <Dropdown.Header className="mb-3">Sector</Dropdown.Header>
                    <Dropdown.Toggle variant="white">
                      {sectorOptions.find((s) => s.id === selectedSector)?.title || "Select Sector"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setSelectedSector(null)}>Select Sector</Dropdown.Item>
                      {sectorOptions.map((s, key) => (
                        <Dropdown.Item key={key} onClick={() => setSelectedSector(s.id)}>
                          {s.title}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              )}
            </Row>
          </Stack>
        )}
        {/* RESULTS */}
        <Row>
          {visibleItems.map((item, k) => {
            const { title, publish_date, url_slug } = item;
            let href;
            if (source === "career_advice" && currentCategory) {
              href = `/career-advice/${currentCategory}/${url_slug}`;
            } else {
              href = getRoute(
                source === "simple_pages"
                  ? "simplePage"
                  : source === "career_advice"
                  ? "careerAdviceArticle"
                  : source === "industry_insights"
                  ? "industryInsightPage"
                  : "blogPost",
                { url_slug },
              );
            }

            return (
              <Col key={`${url_slug}-${k}`} xs={12} md={4} className="mb-4">
                <BlogFeedCard
                  className="h-100"
                  title={title}
                  img={"card_image" in item ? item.card_image : item.image}
                  published_at={publish_date}
                  // href={getRoute(
                  //   source === "simple_pages"
                  //     ? "simplePage"
                  //     : source === "career_advice"
                  //     ? "careerAdviceArticle"
                  //     : "blogPost",
                  //   { url_slug },
                  // )}
                  href={href}
                />
              </Col>
            );
          })}
        </Row>
        {/* LOAD MORE */}
        {showLoadMore && limit < _afterSearch.length && (
          <Stack className={clsx(classes.feed__footer)}>
            <Button variant="secondary" onClick={loadMore}>
              Load More
            </Button>
          </Stack>
        )}
      </Container>
    </div>
  );
}
